import { create } from 'zustand';

import type { User } from '@/entities/user';

/**
 * Global in-memory session snapshot. Tokens live in `tokenStorage.ts`; this
 * store only carries the hydrated `user` + lifecycle `status` so the rest of
 * the app doesn't need to think about localStorage.
 *
 * Status transitions:
 *   unknown     — app is still booting / rehydrating from `/auth/me`
 *   anonymous   — no valid session; show login
 *   authenticated — `user` is guaranteed non-null
 *
 * See TECHNICAL_DESIGN.md §3.8.
 */

export type SessionStatus = 'unknown' | 'anonymous' | 'authenticated';

interface SessionState {
  status: SessionStatus;
  user: User | null;

  /** Called from the auth API after `/auth/login` or `/auth/me` succeeds. */
  setUser: (user: User) => void;
  /** Called from the auth API on logout / hard 401 / failed refresh. */
  clear: () => void;
  /** Called exactly once at boot when we know we have no tokens. */
  markAnonymous: () => void;
}

export const useSessionStore = create<SessionState>((set) => ({
  status: 'unknown',
  user: null,

  setUser: (user) => set({ status: 'authenticated', user }),
  clear: () => set({ status: 'anonymous', user: null }),
  markAnonymous: () => set({ status: 'anonymous', user: null }),
}));

/** Non-hook accessor for use in plain functions (http client, etc.). */
export function getSessionSnapshot(): { status: SessionStatus; user: User | null } {
  const s = useSessionStore.getState();
  return { status: s.status, user: s.user };
}
